/**
 * Sprint 23.6 — AgentProfileTabContent.
 *
 * Pure router: maps the active tab (read from URL hash) to the existing
 * cockpit panels. Sprint 23.6 relocates panels rather than rebuilding them —
 * every tab below uses a Sprint ≤ 23 component without modification, with two
 * small extensions:
 *
 *   • Memories tab passes `defaultFilter="feedback"` so the Learnings view is
 *     the default per Q10 ("Learnings IS memories").
 *   • Pages tab folds Prompts (Sprint 11): both panels render in a single tab
 *     body via a sub-filter selector.
 *
 * Settings tab content is the only net-new wrapper — AgentSettingsTab —
 * because it surfaces 7 previously-hidden AgentConfig fields (Q11). When no
 * tab is selected, the content area collapses entirely so the hero owns the
 * default view.
 */

import { useState, type CSSProperties } from "react";
import { useLocation } from "react-router";
import { TasksPanel } from "../cockpit/TasksPanel";
import { RoutinesPanel } from "../cockpit/RoutinesPanel";
import { PagesPanel } from "../cockpit/PagesPanel";
import { PromptsPanel } from "../cockpit/PromptsPanel";
import { MemoriesPanel } from "../cockpit/MemoriesPanel";
import { FilesPanel } from "../cockpit/FilesPanel";
import { ActivityPanel } from "../cockpit/ActivityPanel";
import { AgentSettingsTab } from "./AgentSettingsTab";
import { readActiveTab } from "./AgentProfileTabs";
import type { LibraryNodeDetail } from "../../../lib/types";

export interface AgentProfileTabContentProps {
  node: LibraryNodeDetail;
  isRoot: boolean;
  canEdit: boolean;
}

export function AgentProfileTabContent({ node, isRoot, canEdit }: AgentProfileTabContentProps) {
  const location = useLocation();
  // Always resolves to a tab (defaults to Tasks) so the content area is
  // never empty on landing.
  const active = readActiveTab(location.hash);

  return (
    <div style={wrap}>
      {active === "tasks" && (
        <TasksPanel nodeId={node.id} includeSubtree={isRoot} />
      )}
      {active === "routines" && <RoutinesPanel nodeId={node.id} />}
      {active === "pages" && <PagesAndPrompts nodeId={node.id} nodeName={node.name} isRoot={isRoot} />}
      {active === "memories" && (
        <MemoriesPanel nodeId={node.id} defaultFilter="feedback" />
      )}
      {active === "files" && <FilesPanel nodeId={node.id} nodeName={node.name} />}
      {active === "activity" && <ActivityPanel nodeId={node.id} />}
      {active === "settings" && <AgentSettingsTab node={node} canEdit={canEdit} />}
    </div>
  );
}

/**
 * Sprint 23.6 — Pages tab body. Folds Sprint 11 Prompts under the Pages tab
 * via a sub-filter ("All / Pages / Prompts"). Pages and Prompts are siblings
 * in the underlying primitive — Sprint 11 already established that.
 */
function PagesAndPrompts({
  nodeId,
  nodeName,
  isRoot,
}: {
  nodeId: string;
  nodeName: string;
  isRoot: boolean;
}) {
  const [view, setView] = useState<"all" | "pages" | "prompts">("all");

  return (
    <div style={pagesWrap}>
      <div style={filterRow}>
        <FilterChip active={view === "all"} onClick={() => setView("all")}>
          All
        </FilterChip>
        <FilterChip active={view === "pages"} onClick={() => setView("pages")}>
          Pages
        </FilterChip>
        <FilterChip active={view === "prompts"} onClick={() => setView("prompts")}>
          Prompts
        </FilterChip>
      </div>
      {(view === "all" || view === "pages") && (
        <PagesPanel nodeId={nodeId} workspaceWide={isRoot} />
      )}
      {(view === "all" || view === "prompts") && (
        <PromptsPanel nodeId={nodeId} nodeName={nodeName} />
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={active ? chipActive : chipIdle}
    >
      {children}
    </button>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: "14px 0",
  flex: 1,
};

const pagesWrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const filterRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const chipBase: CSSProperties = {
  fontSize: 11,
  padding: "3px 9px",
  borderRadius: 999,
  border: "0.5px solid transparent",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const chipIdle: CSSProperties = chipBase;

const chipActive: CSSProperties = {
  ...chipBase,
  background: "var(--color-background-secondary)",
  borderColor: "var(--color-border-tertiary)",
  color: "var(--color-text-primary)",
  fontWeight: 500,
};
